import type { NextRequest } from "next/server";

/** Countries that always receive mock data (never hit OpenAI). */
export const BLOCKED_COUNTRIES = new Set([
  "LA", "NG", "CG", "MR", "SD", "PS", "AZ", "TN", "TR", "LB", "SY", "AF", "DZ",
  "BR", "TH", "MX", "BH", "BD", "BO", "EG", "IN", "ID", "IR", "IQ", "JO", "KW",
  "LY", "MY", "MA", "OM", "PK", "PH", "QA", "SA", "ZA", "AE", "AX", "RU", "BY", "XK", "ET", "TZ"
]);

/** Cloudflare / Tor unknowns — not real ISO countries. */
const INVALID_CF_COUNTRIES = new Set(["XX", "T1", "A1", "A2", "O1"]);

/**
 * Normalize a country code from CF / Stripe / cookies.
 * Returns null for empty, non-ISO, or CF placeholder codes.
 */
export function normalizeClientCountry(
  raw: string | null | undefined
): string | null {
  const code = String(raw || "")
    .trim()
    .toUpperCase()
    .slice(0, 2);
  if (!code || code.length !== 2) return null;
  if (!/^[A-Z]{2}$/.test(code)) return null;
  if (INVALID_CF_COUNTRIES.has(code)) return null;
  return code;
}

export function getClientCountry(req: NextRequest): string {
  return normalizeClientCountry(req.headers.get("cf-ipcountry")) || "";
}

/** Prefer CF header; fall back to middleware `cf-country` cookie. */
export function resolveRequestCountry(req: NextRequest): string | null {
  const fromHeader = normalizeClientCountry(req.headers.get("cf-ipcountry"));
  if (fromHeader) return fromHeader;
  const cookie = req.cookies.get("cf-country")?.value;
  return normalizeClientCountry(cookie);
}

/** ISO-2 → flag emoji (LV → 🇱🇻). Empty string if invalid. */
export function countryFlagEmoji(code: string | null | undefined): string {
  const c = normalizeClientCountry(code);
  if (!c) return "";
  const base = 0x1f1e6;
  return String.fromCodePoint(
    base + (c.charCodeAt(0) - 65),
    base + (c.charCodeAt(1) - 65)
  );
}

/** ISO-2 → localized country name (LV → Latvia). */
export function countryDisplayName(
  code: string | null | undefined,
  locale = "en"
): string | null {
  const c = normalizeClientCountry(code);
  if (!c) return null;
  try {
    const name = new Intl.DisplayNames([locale], { type: "region" }).of(c);
    return name || c;
  } catch {
    return c;
  }
}

/**
 * Client-only: read JS-readable CF country cookie (`cf-country-ui`).
 * Same source as analytics `derived.country` / httpOnly `cf-country`.
 */
export function readClientCfCountry(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)cf-country-ui=([^;]+)/);
  if (!match?.[1]) return null;
  try {
    return normalizeClientCountry(decodeURIComponent(match[1]));
  } catch {
    return normalizeClientCountry(match[1]);
  }
}

export function isBlockedCountry(country: string): boolean {
  return BLOCKED_COUNTRIES.has(country);
}

export function isBlockedGeo(req: NextRequest): boolean {
  return isBlockedCountry(getClientCountry(req));
}
